import React, { useState, useRef, useEffect } from "react";
import {
    ChevronDown,
    ChevronUp,
    Star,
    StarOff,
    RotateCcw,
    RefreshCw,
    FilterX,
    XCircle,
    XCircleIcon
} from "lucide-react";

const ProductFilterInput = ({ field, value, onChange }) => {
    if (field.type === "select") {
        return (
            <div className="min-w-[180px]">
                <label
                    className="text-sm mb-2 block"
                    style={{ color: "var(--main-text)" }}
                >
                    {field.label}
                </label>

                <select
                    value={value ?? ""}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    className="h-10 w-full rounded-lg border px-3 text-sm"
                    style={{
                        background: "var(--input-background)",
                        color: "var(--main-text)",
                        borderColor: "var(--card-border)",
                    }}
                >
                    {field.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        );
    }

    return (
        <div className="min-w-[240px] flex-1">
            <label
                className="text-sm mb-2 block"
                style={{ color: "var(--main-text)" }}
            >
                {field.label}
            </label>

            <input
                type="text"
                value={value ?? ""}
                onChange={(e) => onChange(field.key, e.target.value)}
                placeholder={field.placeholder || "Search..."}
                className="h-10 w-full rounded-lg border px-3 text-sm"
                style={{
                    background: "var(--input-background)",
                    color: "var(--main-text)",
                    borderColor: "var(--card-border)",
                }}
            />
        </div>
    );
};

const LicenseFilterSection = ({
    products,
    selectedProductKey,
    setSelectedProductKey,
    defaultProductKey,
    setDefaultProductKey,
    selectedHandler,
    tableFilters,
    handleFilterChange,
    resetFilters,
    fetchTableRows,
}) => {
    const [isProductDropdownOpen, setIsProductDropdownOpen] =
        useState(false);

    const dropdownRef = useRef(null);

    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsProductDropdownOpen(false);
            }
        };

        document.addEventListener(
            "mousedown",
            handleClickOutside
        );

        return () =>
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
    }, []);

    const handleRefresh = async () => {
        setIsRefreshing(true);

        try {
            await fetchTableRows();
        } finally {
            setTimeout(() => {
                setIsRefreshing(false);
            }, 400);
        }
    };

    return (
        <div
            className="mb-6 rounded-lg border p-5"
            style={{
                background: "var(--card-bg)",
                borderColor: "var(--card-border)",
            }}
        >
            <div className="flex items-center justify-between mb-6">

                <h2
                    className="text-lg font-semibold"
                    style={{ color: "var(--main-text)" }}
                >
                    Filters
                </h2>

                {/* Action Buttons */}
                <div className="flex gap-2">
                    <button
                        onClick={resetFilters}
                        className="
                        h-10 rounded-lg border px-4 text-sm font-medium
                        inline-flex items-center gap-2
                        transition-all duration-200
                        hover:-translate-y-0.5
                        hover:shadow-md
                        hover:bg-red-50
                        hover:text-red-600
                        hover:border-red-300
                        active:scale-95"
                        style={{
                            background: "var(--card-bg)",
                            color: "var(--main-text)",
                            borderColor: "var(--card-border)",
                        }}
                    >
                        <XCircle
                            size={14}
                            className="transition-colors duration-200"
                        />
                        Reset All
                    </button>

                    <button
                        onClick={handleRefresh}
                        className="
                        h-10 rounded-lg px-4 text-sm font-medium text-white
                        inline-flex items-center gap-2
                        transition-all duration-200
                        hover:-translate-y-0.5
                        hover:shadow-md
                        active:scale-95"
                        style={{
                            background: "var(--brand)",
                        }}
                    >
                        <RefreshCw
                            size={14}
                            className={isRefreshing ? "animate-spin" : ""}
                        />
                        Refresh
                    </button>                </div>
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap items-end gap-4">

                {/* Product Dropdown */}
                <div className="min-w-[220px]">
                    <label
                        className="text-sm mb-2 block"
                        style={{ color: "var(--main-text)" }}
                    >
                        Product
                    </label>

                    {/* <select
                        value={selectedProductKey || ""}
                        onChange={(e) =>
                            setSelectedProductKey(e.target.value)
                        }
                        className="h-10 w-full rounded-lg border px-3 text-sm"
                        style={{
                            background: "var(--input-background)",
                            color: "var(--main-text)",
                            borderColor: "var(--card-border)",
                        }}
                    >

                        <option value="">
                            Select Product
                        </option>
                        {products.map((product) => (
                            <option
                                key={product.key}
                                value={product.key}
                            >
                                {product.label.replace(" License Generation", "")}
                            </option>
                        ))}
                    </select> */}

                    <div
                        ref={dropdownRef}
                        className="relative w-[260px]"
                    >
                        <button
                            type="button"
                            onClick={() =>
                                setIsProductDropdownOpen((prev) => !prev)
                            }
                            className="h-10 w-full rounded-lg border px-3 flex items-center justify-between text-sm"
                            style={{
                                background: "var(--input-background)",
                                color: "var(--main-text)",
                                borderColor: "var(--card-border)",
                            }}
                        >
                            <span>
                                {products.find(
                                    (p) => p.key === selectedProductKey
                                )?.label?.replace(
                                    " License Generation",
                                    ""
                                ) || "Select Product"}
                            </span>

                            {/* <ChevronDown size={16} /> */}
                            {isProductDropdownOpen ? (
                                <ChevronUp size={16} />
                            ) : (
                                <ChevronDown size={16} />
                            )}
                        </button>

                        {isProductDropdownOpen && (
                            <div
                                className="absolute z-50 mt-2 w-full rounded-lg border overflow-hidden"
                                style={{
                                    background: "var(--card-bg)",
                                    borderColor: "var(--card-border)",
                                    boxShadow: "var(--shadow-dropdown)",
                                }}
                            >
                                {products.map((product) => (
                                    <div
                                        key={product.key}
                                        className="flex items-center text-sm justify-between px-3 py-2 cursor-pointer table-row-hover"
                                        onClick={() => {
                                            setSelectedProductKey(
                                                product.key
                                            );
                                            setIsProductDropdownOpen(
                                                false
                                            );
                                        }}
                                    >
                                        <span>
                                            {product.label.replace(
                                                " License Generation",
                                                ""
                                            )}
                                        </span>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                if (defaultProductKey === product.key) {
                                                    localStorage.removeItem(
                                                        "defaultLicenseProduct"
                                                    );

                                                    setDefaultProductKey("");
                                                } else {
                                                    localStorage.setItem(
                                                        "defaultLicenseProduct",
                                                        product.key
                                                    );

                                                    setDefaultProductKey(product.key);
                                                }
                                            }}
                                        >
                                            {defaultProductKey ===
                                                product.key ? (
                                                <Star
                                                    size={15}
                                                    className="text-amber-500 fill-amber-500"
                                                />
                                            ) : (
                                                <StarOff
                                                    size={15}
                                                    style={{
                                                        color: "var(--main-text-muted)",
                                                    }}
                                                />
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                </div>

                {selectedProductKey && (
                    <>

                        <ProductFilterInput
                            field={{
                                key: "search",
                                label: "Search",
                                type: "search",
                                placeholder: "Search by License ID, description or client name",
                            }}
                            value={tableFilters.search ?? ""}
                            onChange={handleFilterChange}
                        />

                        <ProductFilterInput
                            field={{
                                key: "licenseType",
                                label: "License Type",
                                type: "select",
                                options: [
                                    { value: "all", label: "All" },
                                    { value: "TRIAL", label: "Trial" },
                                    { value: "PERPETUAL", label: "Perpetual" },
                                ],
                            }}
                            value={tableFilters.licenseType ?? "all"}
                            onChange={handleFilterChange}
                        />

                        <ProductFilterInput
                            field={{
                                key: "status",
                                label: "Status",
                                type: "select",
                                options: [
                                    { value: "all", label: "All" },
                                    { value: "ACTIVE", label: "Active" },
                                    { value: "EXPIRED", label: "Expired" },
                                ],
                            }}
                            value={tableFilters.status ?? "all"}
                            onChange={handleFilterChange}
                        />

                        {/* Product Specific Filters */}
                        {selectedHandler?.filterDefinitions?.map((field) => (
                            <ProductFilterInput
                                key={field.key}
                                field={field}
                                value={tableFilters[field.key]}
                                onChange={handleFilterChange}
                            />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

export default LicenseFilterSection;